import type { ReactNode } from 'react';
import { useFmt } from '@/lib/useFmt.js';

/**
 * Two visual layers per release card:
 *   1. Hero — big version number + relative timestamp (the "what is this" bit)
 *   2. Changelog block — full-width, left-aligned, pre-line for multi-line content
 *   3. Compact 3-col stat grid (date / size / file count)
 *
 * Originally inline inside ReleasesPage; pulled out so the page-level
 * file stays focused on draft / live orchestration.
 */

export interface ManifestSummary {
  version: string;
  min_shell_version: string | null;
  released_at: string;
  changelog: string | null;
  size_bytes: number;
  file_count: number;
}

interface Props {
  accent: 'amber' | 'slate';
  icon: string;
  title: string;
  statusBadge?: { text: string; tone: 'amber' | 'felt' } | undefined;
  manifest: ManifestSummary | null;
  emptyTitle: string;
  emptyHint: string;
  versionColor: string;
  changelogColor: string;
  metaColor: string;
  actions?: ReactNode | undefined;
}

export function ReleaseCard({
  accent,
  icon,
  title,
  statusBadge,
  manifest,
  emptyTitle,
  emptyHint,
  versionColor,
  changelogColor,
  metaColor,
  actions,
}: Props): JSX.Element {
  const { fmtDate, fmtDateTime } = useFmt();
  const containerClasses =
    accent === 'amber'
      ? 'bg-amber-900/10 border-amber-700/40'
      : 'bg-white/[0.04] border-white/10';
  const titleClasses = accent === 'amber' ? 'text-amber-300' : 'text-slate-300';
  const badgeClasses =
    statusBadge?.tone === 'amber'
      ? 'bg-amber-900/50 text-amber-200 border-amber-700/40'
      : 'bg-felt-700/30 text-felt-400 border-felt-700/40';

  return (
    <section className={`${containerClasses} border rounded-xl p-5`}>
      <div className="flex items-center gap-2 mb-4">
        <h2 className={`text-sm font-semibold ${titleClasses}`}>
          {icon} {title}
        </h2>
        {statusBadge !== undefined && (
          <span className={`text-xs px-2 py-0.5 rounded border ${badgeClasses}`}>
            {statusBadge.text}
          </span>
        )}
      </div>

      {manifest === null ? (
        <div className="text-sm text-slate-500 py-8 text-center">
          <p className="mb-1">{emptyTitle}</p>
          <p className="text-xs">{emptyHint}</p>
        </div>
      ) : (
        <>
          <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
            <code
              className={`font-mono text-xl sm:text-2xl font-semibold ${versionColor} break-all`}
            >
              v{manifest.version}
            </code>
            <span className="text-xs text-slate-500">
              {relativeTime(manifest.released_at, fmtDate)}
            </span>
          </div>

          <div className="mb-4">
            <div className="text-xs text-slate-500 mb-1.5">变更说明</div>
            {manifest.changelog !== null && manifest.changelog !== '' ? (
              <div
                className={`text-sm ${changelogColor} whitespace-pre-line leading-relaxed bg-white/[0.03] rounded-lg p-3 border border-white/[0.08] max-h-64 overflow-y-auto`}
              >
                {manifest.changelog}
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">未填写</p>
            )}
          </div>

          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <Stat label="发布时间" color={metaColor} value={fmtDateTime(manifest.released_at)} />
            <Stat label="包大小" color={metaColor} value={formatBytes(manifest.size_bytes)} />
            <Stat label="文件数" color={metaColor} value={String(manifest.file_count)} />
            <Stat
              label="最低桌面壳"
              color={metaColor}
              value={manifest.min_shell_version === null ? '旧格式' : `v${manifest.min_shell_version}`}
            />
          </dl>

          {actions}
        </>
      )}
    </section>
  );
}

function Stat({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}): JSX.Element {
  return (
    <div>
      <dt className="text-slate-500 mb-0.5">{label}</dt>
      <dd className={`${color} break-all`}>{value}</dd>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

/** "5 分钟前" / "2 小时前" / "3 天前" / fall back to fmtDate for older entries. */
function relativeTime(iso: string, fmtDate: (s: string) => string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const deltaSec = Math.max(0, (Date.now() - then) / 1000);
  if (deltaSec < 60) return '刚刚';
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)} 分钟前`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)} 小时前`;
  if (deltaSec < 86400 * 7) return `${Math.floor(deltaSec / 86400)} 天前`;
  return fmtDate(iso);
}
